import { useState } from "react";
import { useAuth } from "./useAuth";
import { 
  useGeolocationSettingsQuery, 
  useGeofencesQuery,
  useUpdateGeolocationSettings,
  useCreateGeofence,
  useUpdateGeofence,
  useDeleteGeofence,
  GeolocationSettings,
  Geofence,
  GeofenceFilter
} from "./geolocation";

// Re-export types for backward compatibility
export type { GeolocationSettings, Geofence, GeofenceFilter };

export function useGeolocationSettings() {
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.id;
  const [filter, setFilter] = useState<GeofenceFilter>('active');

  // Query hooks
  const settingsQuery = useGeolocationSettingsQuery(companyId);
  const geofencesQuery = useGeofencesQuery(companyId, filter);

  // Mutation hooks
  const updateSettingsMutation = useUpdateGeolocationSettings(companyId);
  const createGeofenceMutation = useCreateGeofence(companyId);
  const updateGeofenceMutation = useUpdateGeofence(companyId);
  const deleteGeofenceMutation = useDeleteGeofence(companyId);

  // Wrapper functions to maintain backward compatibility
  const updateSettings = async (patch: Partial<GeolocationSettings>) => {
    return updateSettingsMutation.mutateAsync(patch);
  };

  const createGeofence = async (geofence: Omit<Geofence, 'id' | 'company_id' | 'created_at' | 'updated_at'>) => {
    return createGeofenceMutation.mutateAsync(geofence);
  };

  const updateGeofence = async (id: string, patch: Partial<Omit<Geofence, 'id' | 'company_id' | 'created_at' | 'updated_at'>>) => {
    return updateGeofenceMutation.mutateAsync({ id, patch });
  };

  const deleteGeofence = async (id: string) => {
    return deleteGeofenceMutation.mutateAsync(id);
  };

  const setGeofenceFilter = (newFilter: GeofenceFilter) => {
    setFilter(newFilter);
  };

  const refresh = () => {
    settingsQuery.refetch();
    geofencesQuery.refetch();
  };

  return {
    settings: settingsQuery.data || { geo_enabled: false, geo_strict_mode: false, geo_default_radius_meters: 100 },
    geofences: geofencesQuery.data || [],
    isLoading: settingsQuery.isLoading || geofencesQuery.isLoading,
    filter,
    setGeofenceFilter,
    updateSettings,
    createGeofence,
    updateGeofence,
    deleteGeofence,
    refresh,
  };
}
